import { LinearProgress, Stack, ToggleButtonGroup } from '@mui/material';
import _ from 'lodash';
import {
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DetailedSearch } from '../../../components/search-input/DetailedSearchBar';
import {
  InspectionToggleButton,
  PrimaryOutlinedButton,
  SecondaryTextButton,
  TetraPrimaryLoadingButton,
} from '../../../components/style/ButtonStyle';
import { PrettyScrollbarTableWrap } from '../../../components/style/TableStyle';
import {
  CountingText,
  FormTitle,
  SubTitle,
} from '../../../components/style/TextStyle';
import {
  DefaultImgIcon,
  DownloadFileIcon,
  DownloadIcon,
  DownloadInfoIcon,
  PencilIcon,
} from '../../../layouts/dashboard/nav/IconButton';

import {
  QueryClient,
  QueryClientProvider,
  useInfiniteQuery,
} from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { getYear, subYears } from 'date-fns';
import { MaterialReactTable } from 'material-react-table';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Empty from '../../../components/common/Empty';
import { TableBackground } from '../../../components/style/CommonStyle';
import { useAuthDownload } from '../../../hooks/useAuthAxios';
import { selectAccessToken } from '../../../store/authSlice';
import { APP_BAR_DESKTOP, OwnershipTypeListLabel } from '../../../utils/enum';
import { fDate } from '../../../utils/formatTime';
import { sliceSerialNumber } from '../../../utils/helper';

const queryClient = new QueryClient();

export default function InspectionListPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <InspectionList />
    </QueryClientProvider>
  );
}
const PAGE_SIZE = 10;

function InspectionList() {
  const [search, setSearch] = useState('');
  const [year, setYear] = useState(0);
  const navigate = useNavigate();
  const startDate = new Date(getYear(subYears(new Date(), year)), 0, 1);
  const params = {
    title: search,
    inspectionDateStart: fDate(startDate, 'yyyy-MM-dd'),
    inspectionDateEnd: fDate(new Date(), 'yyyy-MM-dd'),
  };
  const accessToken = useSelector(selectAccessToken);
  const tanstackResult = useInfiniteQuery<AxiosResponse>({
    queryKey: [params],
    queryFn: async ({ pageParam = 0 }) => {
      const resp = await axios({
        method: 'GET',
        url: '/api/v1/inspection',
        params: {
          ...params,
          page: pageParam + 1,
          pageSize: PAGE_SIZE,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return resp.data;
    },
    getNextPageParam: (_lastGroup, groups) => groups.length,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
  const { data, fetchNextPage, isFetching, isLoading } = tanstackResult;

  const inspections = useMemo(() => {
    const list = data?.pages.flatMap(page => _.get(page, 'result', [])) || [];
    const emptyRows = _.times(PAGE_SIZE - list.length, t => ({
      inspectionId: t,
    }));
    return [...list, ...emptyRows];
  }, [data]);

  const totalDBRowCount = _.get(data, 'pages.0.count', 0);
  const totalFetched = inspections.length;

  //called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 400px of the bottom of the table, fetch more data if we can
        if (
          scrollHeight - scrollTop - clientHeight < 400 &&
          !isFetching &&
          totalFetched < totalDBRowCount
        ) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount],
  );

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: number,
  ) => {
    setYear(newAlignment);
  };
  return (
    <Stack
      mx={4}
      spacing={4}
      py={8}
      sx={{ height: `calc(100vh - ${APP_BAR_DESKTOP}px)` }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FormTitle>시설물 점검 리스트</FormTitle>
        <Stack>
          <PrimaryOutlinedButton
            startIcon={<PencilIcon />}
            onClick={() => navigate(`/inspection/bulk`)}
          >
            점검 일괄 등록
          </PrimaryOutlinedButton>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={3}>
        <Stack spacing={2} flex={1}>
          <SubTitle>시설명 또는 관리번호</SubTitle>
          <DetailedSearch
            placeholder="시설명 또는 관리번호를 입력하세요."
            onKeyDown={e => {
              if (e.key === 'Enter') {
                setSearch(e.target.value);
              }
            }}
            onClick={(e, value) => {
              setSearch(value);
            }}
          />
        </Stack>
        <Stack spacing={2} flex={1}>
          <SubTitle>년도 선택</SubTitle>
          <ToggleButtonGroup
            color="primary"
            value={year}
            exclusive
            onChange={handleChange}
            sx={{ width: '100%', gap: '8px' }}
          >
            {[
              { value: 0, label: '당해' },
              { value: 1, label: '2개년' },
              { value: 2, label: '3개년' },
              { value: 3, label: '4개년' },
              { value: 4, label: '5개년' },
              { value: 9, label: '10개년' },
            ].map(check => (
              <InspectionToggleButton
                value={check?.value}
                key={check?.value}
                sx={{ flex: 1 }}
              >
                {check?.label}
              </InspectionToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
        <Stack justifyContent="center">
          <a
            href="https://drive.google.com/file/d/1WpJNjlNFVAzVklm5WiSV5Fpif2fUOJCj/view?usp=drive_link"
            target="_blank"
            rel="noreferrer"
          >
            <SecondaryTextButton
              startIcon={<DownloadInfoIcon />}
              endIcon={<DownloadFileIcon />}
            >
              점검관리 가이드
            </SecondaryTextButton>
          </a>
        </Stack>
      </Stack>
      {totalDBRowCount === 0 ? (
        <Empty />
      ) : (
        <TableBackground>
          <Stack
            direction="row"
            sx={{ w: '100%', mb: 2 }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack>
              <CountingText>총 {totalDBRowCount}건</CountingText>
            </Stack>
            <Stack>
              <DownloadHwpButton
                pageParam={Math.ceil(inspections?.length / PAGE_SIZE)}
                search={search}
                startDate={startDate}
              />
            </Stack>
          </Stack>

          <InspectionTable
            inspections={inspections}
            isFetching={isFetching}
            isLoading={isLoading}
            fetchMoreOnBottomReached={fetchMoreOnBottomReached}
          />
        </TableBackground>
      )}
    </Stack>
  );
}

function InspectionTable({
  inspections,
  isFetching,
  isLoading,
  fetchMoreOnBottomReached,
}) {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  //a check on mount to see if the table is already scrolled to the bottom and immediately needs to fetch more data
  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'serialNumber',
        header: '관리번호',
        Cell: ({ row }: any) => {
          const inspection = row.original;
          return <>{sliceSerialNumber(inspection?.serialNumber) || ''}</>;
        },
      },
      { accessorKey: 'title', header: '시설명', size: 320 },
      { accessorKey: 'addressName', header: '주소', size: 320 },
      { accessorKey: 'occupyingSpace', header: '설치면적' },
      {
        accessorKey: 'dateOfInstall',
        header: '설치일',
        Cell: ({ cell }) => <>{fDate(cell.getValue(), 'yyyy-MM-dd') || '-'}</>,
      },
      {
        accessorKey: 'ownershipType',
        header: '설치유형',
        Cell: ({ row }: any) => {
          const inspection = row.original;
          return (
            <>{_.get(OwnershipTypeListLabel, inspection?.ownershipType, '-')}</>
          );
        },
      },
      {
        accessorKey: 'budget',
        header: '사업비(국비/시비/구비)',
        Cell: ({ row }: any) => {
          const inspection = row.original;
          return (
            <>
              {inspection?.installCostNation || '-'}/
              {inspection?.installCostCity || '-'}/
              {inspection?.installCostDistrict || '-'}
            </>
          );
        },
      },
      {
        accessorKey: 'overviewImageUrl',
        header: '전경사진',
        Cell: ({ row }: any) => {
          const inspection = row.original;
          return (
            <>
              {_.isEmpty(inspection?.overviewImageUrl) ? (
                <DefaultImgIcon />
              ) : (
                <img
                  alt="marker"
                  style={{ maxWidth: 80 }}
                  src={_.get(inspection, 'overviewImageUrl')}
                />
              )}
            </>
          );
        },
      },
      { accessorKey: 'subCategory', header: '기구명' },
      { accessorKey: 'inspectionScore', header: '시설상태' },
      { accessorKey: 'inspectionResultTypes', header: '상태비고' },
      {
        accessorKey: 'inspectionDate',
        header: '점검(보수)일시',
        Cell: ({ cell }) => <>{fDate(cell.getValue(), 'yyyy-MM-dd') || '-'}</>,
      },
      { accessorKey: 'inspectionNote', header: '점검(보수)내용' },
    ],
    [],
  );

  const rowVirtualizerInstanceRef = useRef(null);
  return (
    <PrettyScrollbarTableWrap>
      {isFetching ? <LinearProgress /> : <div style={{ height: 4 }} />}
      <MaterialReactTable
        state={{
          isLoading,
        }}
        initialState={{ density: 'compact' }}
        columns={columns}
        data={inspections}
        enableTopToolbar={false}
        enableBottomToolbar={false}
        enableColumnResizing={true}
        enableColumnVirtualization={false}
        enableGlobalFilterModes={false}
        enablePagination={false}
        enablePinning={false}
        enableRowNumbers={false}
        enableRowVirtualization={true}
        enableColumnActions={false}
        getRowId={original => original.inspectionId as string}
        enableSorting={false}
        muiTableContainerProps={{
          style: {
            maxHeight: 'calc(100vh - 506px)',
          },
          ref: tableContainerRef,
          onScroll: (
            event: UIEvent<HTMLDivElement>, //add an event listener to the table container element
          ) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
        }}
        muiSelectCheckboxProps={{ size: 'small' }}
        muiTableHeadCellProps={{ align: 'center' }}
        muiTableBodyCellProps={{ align: 'center' }}
        rowVirtualizerInstanceRef={rowVirtualizerInstanceRef} //optional
        rowVirtualizerProps={{ overscan: 20 }} //optionally customize the row virtualizer
      />
    </PrettyScrollbarTableWrap>
  );
}

function DownloadHwpButton({ pageParam, search, startDate }) {
  const [download, downloadResult] = useAuthDownload('야외운동기구 관리대장', {
    method: 'GET',
    url: `/api/v1/inspection/hwp`,
    params: {
      page: pageParam + 1,
      pageSize: PAGE_SIZE,
      title: search,
      inspectionDateStart: fDate(startDate, 'yyyy-MM-dd'),
      inspectionDateEnd: fDate(new Date(), 'yyyy-MM-dd'),
    },
  });

  return (
    <>
      {downloadResult.loading ? (
        <TetraPrimaryLoadingButton
          startIcon={<DownloadIcon />}
          loading={true}
          disabled={true}
        >
          다운로드 중...
        </TetraPrimaryLoadingButton>
      ) : (
        <TetraPrimaryLoadingButton
          startIcon={<DownloadIcon />}
          onClick={() => {
            download();
          }}
          loading={false}
          disabled={false}
        >
          야외운동기구 관리대장 다운로드
        </TetraPrimaryLoadingButton>
      )}
    </>
  );
}
